import {
  ResponsiveScatterPlot,
  ScatterPlotDatum,
  ScatterPlotRawSerie,
} from '@nivo/scatterplot'
import styled from '@emotion/styled'
import React from 'react'
import { Spinner, useTheme } from '@ss/meta-core-components'
import { LegendProps } from '@nivo/legends'
import { line } from 'd3-shape'
import { IDimension, ITimeScale, ITrendLine } from '@types'
import { initTheme, MAIN_PALETTE, NumberFormatter } from '@utils'
import { Margin } from '@nivo/core'

interface Props {
  data: ScatterPlotRawSerie<ScatterPlotDatum>[]
  dimensions?: IDimension
  dataCy?: string
  showTrendLine?: ITrendLine
  showLegend?: boolean
  timeScale?: ITimeScale
  axisLegend?: { x: string; y: string }
  customMargin?: Partial<Margin>
}

/**
 *  Componente grafico di tipo scatter plot
 * @param props.data {Array<ScatterPlotRawSerie<ScatterPlotDatum>>} dati da visualizzare
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.dataCy {string=} support test e2e
 * @param props.showTrendLine {ITrendLine=} dati per mostrare la linea mediana ai punti
 * @param props.showLegend {boolean=} flag per mostrare la leggenda
 * @param props.timeScale {ITimeScale= } flag per time scale
 * @param props.axisLegend {({ x: string, y: string })= } legenda per il dato del asse x e del asse y
 * @component
 */
export const ScatterPlot: React.FC<Props> = (props) => {
  const {
    data,
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    showLegend = false,
    showTrendLine = [],
    timeScale,
    axisLegend = { x: undefined, y: undefined },
  } = props

  const { type } = useTheme()

  const lineLayer = ({
    xScale,
    yScale,
  }: {
    xScale: Function
    yScale: Function
  }): JSX.Element => {
    if (!showTrendLine || showTrendLine.length === 0) return <></>
    const lineGenerator = line()
      .x((d: number[]) => xScale(d[0]))
      .y((d: number[]) => yScale(d[1]))

    return (
      <path
        d={lineGenerator(showTrendLine) || undefined}
        fill={type === 'dark' ? 'white' : 'black'}
        strokeWidth={1}
        stroke={type === 'dark' ? 'white' : 'black'}
      />
    )
  }
  const layers = [
    'grid',
    'axes',
    'nodes',
    lineLayer,
    'markers',
    'mesh',
    'legends',
    'annotations',
  ]

  const chartProps: any = {
    data: [...data],
    margin: props.customMargin || { top: 60, right: 140, bottom: 70, left: 90 },
    xScale: { type: 'linear', min: -10, max: 'auto' },
    yScale: { type: 'linear', min: -10, max: 'auto' },
    axisTop: null,
    axisRight: null,
    axisBottom: {
      legend: axisLegend.x,
      legendPosition: 'middle',
      legendOffset: 46,
      format: (value: number | string) => {
        if (typeof value === 'number') {
          if (value <= 0) return NumberFormatter(value, 'KGM')
          if (value < 0.001) return NumberFormatter(value, 'EXP')
          if (value > 9999) return NumberFormatter(value, 'KGM')
          return value
        }
        return value
      },
    },
    axisLeft: {
      orient: 'left',
      legend: axisLegend.y,
      legendPosition: 'middle',
      legendOffset: -60,
      format: (value: number | string) => {
        if (typeof value === 'number') {
          if (value <= 0) return NumberFormatter(value, 'KGM')
          if (value < 0.001) return NumberFormatter(value, 'EXP')
          if (value > 9999) return NumberFormatter(value, 'KGM')
          return value
        }
        return value
      },
    },
    layers,
    colors: MAIN_PALETTE,
  }

  const timeAxis = timeScale
    ? {
        [`${timeScale.axis}Scale`]: {
          type: 'time',
          format: timeScale.format,
          useUTC: false,
          precision: 'day',
        },
        [`${timeScale.axis}Format`]: `time:${timeScale.format}`,
        [`axis${timeScale.axis === 'x' ? 'Bottom' : 'Left'}`]: {
          format: '%b %d',
          tickValues: 'every 2 days',
          legend: axisLegend.x || 'Time',
          legendPosition: 'middle',
          legendOffset: 46,
        },
      }
    : undefined

  const legends: LegendProps[] = showLegend
    ? [
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 130,
          translateY: 0,
          itemWidth: 100,
          itemHeight: 12,
          itemsSpacing: 5,
          itemDirection: 'left-to-right',
          symbolSize: 12,
          itemOpacity: 0.85,
          symbolShape: 'circle',
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1,
              },
            },
          ],
        },
      ]
    : []

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}`}
    >
      {data ? (
        <ResponsiveScatterPlot
          {...chartProps}
          legends={legends}
          {...timeAxis}
          theme={initTheme(type)}
          xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          mar
        />
      ) : (
        <Loading>
          <Loading>
            <Spinner data-cy={`${dataCy}-loading`} />
          </Loading>
          <Loading>LOADING</Loading>
        </Loading>
      )}
    </Container>
  )
}

const Container = styled.div<{ height: string; width: string }>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`

const Loading = styled.div`
  display: grid;
  place-self: center;
  gap: 10px;
`
